'use client';

import { ChevronLeft } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import logoWhite from '@/app/assets/imgs/logo-white.png';
import { toast } from '@/app/components/ui/use-toast';
import { cn } from '@/app/utils';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { useEarnStatus } from '../_hooks/use-earn-status';

const navItems = [
  { href: '/earn/activities', labelKey: 'header.activityCenter' },
  { href: '/earn/profile', labelKey: 'header.personalCenter' },
] as const;

export function EarnHeader() {
  const t = useTranslations('earn');
  const pathname = usePathname();
  const router = useRouter();
  const { refreshStatus, status } = useEarnStatus();
  const [checkingHref, setCheckingHref] = useState<string | null>(null);
  const isActivityDetail = pathname.includes('/earn/activities/');
  const showBackHeader = isActivityDetail;

  const handleProtectedNavigate = async (href: string) => {
    if (checkingHref) return;

    setCheckingHref(href);

    try {
      const nextStatus =
        status && status !== 'reviewing' ? status : (await refreshStatus({ force: status === 'reviewing' })).status;

      if (nextStatus === 'approved') {
        router.push(href);
        return;
      }

      toast({ type: 'error', message: t('header.creatorOnlyToast') });
    } catch {
      toast({ type: 'error', message: t('header.creatorOnlyToast') });
    } finally {
      setCheckingHref(null);
    }
  };

  return (
    <header className="absolute inset-x-0 top-0 z-30">
      <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-12 sm:h-16 sm:px-5">
        {showBackHeader ? (
          <Link
            href="/earn/activities"
            className="inline-flex h-11 items-center gap-2 rounded-full bg-white/28 px-6 font-semibold text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur-xl transition hover:bg-white/36 sm:h-10 sm:px-4"
          >
            <ChevronLeft className="size-5" />
            {t('activity.backToActivities')}
          </Link>
        ) : (
          <Link href="/" className="relative h-8 w-[82px]" aria-label="Vidu home">
            <Image src={logoWhite} alt="Vidu" fill sizes="82px" className="object-contain" priority />
          </Link>
        )}

        {!showBackHeader && (
          <nav className="flex items-center gap-5 sm:gap-2">
            {navItems.map((item) => {
              const active = pathname.includes(item.href);

              return (
                <button
                  key={item.href}
                  type="button"
                  disabled={checkingHref === item.href}
                  onClick={() => handleProtectedNavigate(item.href)}
                  className={cn(
                    'inline-flex h-11 min-w-[146px] items-center justify-center rounded-full px-6 font-semibold text-[15px] text-white transition sm:h-10 sm:min-w-0 sm:px-4 sm:text-sm',
                    active
                      ? 'bg-[#1396ff] shadow-[0_14px_32px_rgba(19,150,255,0.28)] hover:bg-[#2aa2ff]'
                      : 'bg-white/32 backdrop-blur-xl hover:bg-white/40',
                    checkingHref === item.href && 'cursor-wait opacity-72',
                  )}
                >
                  {t(item.labelKey)}
                </button>
              );
            })}
          </nav>
        )}
      </div>
    </header>
  );
}
